import { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'outline' | 'ghost' | 'white';
type Size = 'sm' | 'md' | 'lg' | 'icon';

const variantClasses: Record<Variant, string> = {
    primary: 'bg-[var(--eb-primary)] text-white hover:opacity-90 border border-transparent',
    secondary: 'bg-[var(--eb-primary-soft)] text-[var(--eb-primary)] hover:bg-[var(--eb-primary-light)] border border-transparent',
    success: 'bg-emerald-600 text-white hover:bg-emerald-700 border border-transparent',
    danger: 'bg-red-600 text-white hover:bg-red-700 border border-transparent',
    warning: 'bg-amber-500 text-white hover:bg-amber-600 border border-transparent',
    outline: 'bg-transparent text-[var(--eb-text)] border border-[var(--eb-border)] hover:bg-[var(--eb-bg)]',
    ghost: 'bg-transparent text-[var(--eb-text-muted)] hover:bg-[var(--eb-bg)] hover:text-[var(--eb-text)] border border-transparent',
    white: 'bg-white/15 text-white border border-white/30 hover:bg-white/25',
};

const sizeClasses: Record<Size, string> = {
    sm: 'px-3 py-1.5 text-xs gap-1.5',
    md: 'px-4 py-2 text-sm gap-2',
    lg: 'px-5 py-2.5 text-base gap-2',
    icon: 'p-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: Variant;
    size?: Size;
    loading?: boolean;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
}

/**
 * Bouton standardisé Tailwind — remplace MUI Button.
 *
 * Variants : primary | secondary | success | danger | warning | outline | ghost | white
 * Sizes : sm | md | lg | icon
 */
const Button = ({ variant = 'primary', size = 'md', loading, startIcon, endIcon, className = '', children, disabled, ...rest }: ButtonProps) => (
    <button
        className={`inline-flex items-center justify-center font-medium rounded-lg transition-all duration-150 select-none
            focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--eb-primary)] focus-visible:ring-offset-1
            disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none
            ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
        disabled={disabled || loading}
        {...rest}
    >
        {loading ? <Loader2 size={16} className="animate-spin" /> : startIcon}
        {children}
        {!loading && endIcon}
    </button>
);

export default Button;
